import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { CampoDeSenha } from '@/components/CampoDeSenha/CampoDeSenha';
import { alterarSenha } from '@/features/autenticacao/api/autenticacaoApi';
import { useSessao } from '@/features/autenticacao/useSessao';
import { descreverErro } from '@/lib/erros';

import estilos from './PaginaDeLogin.module.css';

/**
 * Troca de senha. Especificacao V3.1, item 15.4.
 *
 * Reaproveita o estilo do login de proposito: e a mesma situacao para o usuario,
 * uma tela de credencial antes de entrar na aplicacao.
 *
 * TROCAR A SENHA DERRUBA TODAS AS SESSOES, inclusive esta — o servidor revoga as
 * familias de refresh token. Por isso o desfecho de sucesso e voltar ao login, e
 * nao seguir para a aplicacao: continuar com o token atual daria erro na primeira
 * renovacao, uns minutos depois, sem relacao aparente com a troca de senha.
 */

/** Minimo do OWASP na ausencia de MFA. Mesma constante da PoliticaDeSenha. */
const TAMANHO_MINIMO_DE_SENHA = 15;

interface CampoDeTroca {
  senhaAtual: string;
  novaSenha: string;
  confirmacao: string;
}

export function PaginaDeTrocaDeSenha() {
  const { deveTrocarSenha, sair } = useSessao();
  const navegar = useNavigate();

  const [falha, definirFalha] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<CampoDeTroca>({
    defaultValues: { senhaAtual: '', novaSenha: '', confirmacao: '' },
  });

  const enviar = handleSubmit(async ({ senhaAtual, novaSenha }) => {
    definirFalha(null);

    try {
      await alterarSenha(senhaAtual, novaSenha);

      // `sair` limpa a sessao local e o cache. A chamada ao logout do servidor
      // dentro dele vai falhar em silencio, porque as familias acabaram de ser
      // revogadas pela propria troca — e nao ha nada a informar.
      await sair();

      void navegar('/login', { replace: true });
    } catch (erro) {
      definirFalha(descreverErro(erro));
    }
  });

  return (
    <main className={estilos.tela}>
      <div className={estilos.cartao}>
        <h1 className={estilos.titulo}>Trocar a senha</h1>
        <p className={estilos.apoio}>
          {deveTrocarSenha
            ? 'Sua senha foi definida por um administrador. Escolha uma senha sua para continuar.'
            : 'Escolha uma nova senha. Todas as sessoes abertas serao encerradas.'}
        </p>

        {falha !== null && (
          <p className={estilos.aviso} role="alert">
            {falha}
          </p>
        )}

        <form
          onSubmit={(evento) => {
            void enviar(evento);
          }}
          noValidate
        >
          <div className={estilos.campo}>
            <label className={estilos.rotulo} htmlFor="senhaAtual">
              Senha atual
            </label>
            <CampoDeSenha
              id="senhaAtual"
              autoComplete="current-password"
              invalido={errors.senhaAtual !== undefined}
              aria-invalid={errors.senhaAtual !== undefined}
              {...register('senhaAtual', { required: 'Informe a senha atual.' })}
            />
            {errors.senhaAtual !== undefined && (
              <span className={estilos.erroDeCampo}>{errors.senhaAtual.message}</span>
            )}
          </div>

          <div className={estilos.campo}>
            <label className={estilos.rotulo} htmlFor="novaSenha">
              Nova senha
            </label>
            <CampoDeSenha
              id="novaSenha"
              autoComplete="new-password"
              invalido={errors.novaSenha !== undefined}
              aria-invalid={errors.novaSenha !== undefined}
              aria-describedby="ajuda-senha"
              {...register('novaSenha', {
                required: 'Informe a nova senha.',
                minLength: {
                  value: TAMANHO_MINIMO_DE_SENHA,
                  message: `A senha precisa ter ao menos ${String(TAMANHO_MINIMO_DE_SENHA)} caracteres.`,
                },
              })}
            />
            {/* Sem regra de composicao: o OWASP e explicito de que comprimento
                vence composicao, e exigir simbolo produz senha pior e anotada. */}
            <span className={estilos.erroDeCampo} id="ajuda-senha">
              {errors.novaSenha?.message ??
                `Ao menos ${String(TAMANHO_MINIMO_DE_SENHA)} caracteres. Uma frase funciona bem.`}
            </span>
          </div>

          <div className={estilos.campo}>
            <label className={estilos.rotulo} htmlFor="confirmacao">
              Repita a nova senha
            </label>
            <CampoDeSenha
              id="confirmacao"
              autoComplete="new-password"
              invalido={errors.confirmacao !== undefined}
              aria-invalid={errors.confirmacao !== undefined}
              {...register('confirmacao', {
                required: 'Repita a nova senha.',
                validate: (valor) =>
                  valor === getValues('novaSenha') || 'As duas senhas precisam ser iguais.',
              })}
            />
            {errors.confirmacao !== undefined && (
              <span className={estilos.erroDeCampo}>{errors.confirmacao.message}</span>
            )}
          </div>

          <button className={estilos.botao} type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Trocando...' : 'Trocar a senha'}
          </button>
        </form>
      </div>
    </main>
  );
}
